import React from "react";
import { makeStyles } from "@material-ui/core/styles";
import MenuItem from "@material-ui/core/MenuItem";
import ListItemText from "@material-ui/core/ListItemText";
import Checkbox from "@material-ui/core/Checkbox";
import { blue } from "@material-ui/core/colors";
import { withStyles } from "@material-ui/styles";
import "./AccessTree.scss";

export const useStyles = makeStyles(() => ({
  groupParent: {
    padding: 0,
    minHeight: 45
  },
  groupChild: {
    padding: 0,
    paddingLeft: 10,
    margin: 0,
    height: 37,
    minHeight: 37
  },
  groupSubChild: {
    padding: 0,
    paddingLeft: 20,
    margin: 0,
    height: 30,
    minHeight: 30
  }
}));

export const BlueCheckbox = withStyles({
  root: {
    color: blue[400],
    "&$checked": {
      color: blue[600]
    }
  },
  checked: {}
})(props => <Checkbox color="default" {...props} />);

/**
 * Pure presentational checkbox tree. Renders region -> area -> business rows
 * with indeterminate-state propagation. Extracted from MultiSelectFilter so
 * the user-edit Assignment Panel can reuse the exact indeterminate
 * bookkeeping without going through a Select popover.
 *
 * Props are read-only: the parent owns state and passes down `options`
 * (with `checked` flags already set) plus toggle callbacks. This matches the
 * MultiSelectFilter control shape verbatim, so MultiSelectFilter could also
 * be refactored to use AccessTree in a later cleanup pass.
 */
const AccessTree = ({
  options,
  showOnlyChidren = false,
  showOnlySubChidren = false,
  onToggleParent,
  onToggleChild,
  onToggleSubChild,
  classes = useStyles(),
  lockedBusinessIds = [],
  renderParentLabel,
  renderChildLabel,
  renderSubChildLabel
}) => {
  const lockedSet = new Set(lockedBusinessIds);
  const isParentChecked = parent => parent.checked;
  const isChildChecked = (parent, child) => child.checked;
  const isInParentAtLeastOneChildChecked = parent => parent.children.findIndex(el => el.checked) > -1;
  const isInParentAtLeastOneSubChildChecked = parent =>
    parent.children.some(c => c.subChildren?.some(sc => sc.checked));
  const isInChildAtLeastOneSubChildChecked = (parent, child) =>
    child.subChildren ? child.subChildren.some(el => el.checked) : false;

  return (
    <div className="access-tree">
      {options.map(v => (
        <div key={v.value} className="access-tree__parent">
          <MenuItem
            value={v.value}
            className={classes.groupParent}
            style={{ display: `${showOnlyChidren || showOnlySubChidren ? "none" : "flex"}` }}
            onClick={() => onToggleParent && onToggleParent(v)}
          >
            <BlueCheckbox
              indeterminate={
                !isParentChecked(v) && (isInParentAtLeastOneChildChecked(v) || isInParentAtLeastOneSubChildChecked(v))
              }
              checked={
                isParentChecked(v) || isInParentAtLeastOneChildChecked(v) || isInParentAtLeastOneSubChildChecked(v)
              }
            />
            <ListItemText primary={renderParentLabel ? renderParentLabel(v) : v.label} />
          </MenuItem>
          {v.children.map(c => (
            <div key={c.value} className="access-tree__child">
              <MenuItem
                value={c.value}
                style={{ display: `${showOnlySubChidren ? "none" : "flex"}` }}
                className={showOnlyChidren ? classes.groupParent : classes.groupChild}
                onClick={() => onToggleChild && onToggleChild(v, c)}
              >
                <BlueCheckbox
                  indeterminate={!isChildChecked(v, c) && isInChildAtLeastOneSubChildChecked(v, c)}
                  checked={isChildChecked(v, c) || isInChildAtLeastOneSubChildChecked(v, c)}
                />
                <ListItemText primary={renderChildLabel ? renderChildLabel(v, c) : c.label} />
              </MenuItem>
              {c.subChildren && c.subChildren.length
                ? c.subChildren.map(sc => {
                    const isLocked = lockedSet.has(sc.value) && sc.checked;
                    return (
                      <MenuItem
                        key={sc.value}
                        value={sc.value}
                        className={
                          showOnlySubChidren
                            ? classes.groupParent
                            : showOnlyChidren
                              ? classes.groupChild
                              : classes.groupSubChild
                        }
                        style={isLocked ? { opacity: 0.65, cursor: "default" } : undefined}
                        onClick={() => {
                          if (isLocked) return;
                          onToggleSubChild && onToggleSubChild(v, c, sc);
                        }}
                      >
                        <BlueCheckbox checked={!!sc.checked} disabled={isLocked} />
                        <ListItemText
                          primary={
                            renderSubChildLabel
                              ? renderSubChildLabel(v, c, sc)
                              : isLocked
                                ? `${sc.label} (primary)`
                                : sc.label
                          }
                        />
                      </MenuItem>
                    );
                  })
                : null}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
};

export default AccessTree;
